import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import fetch from 'auth/FetchInterceptor';
import AuthService from 'services/AuthService';
import { getMe } from 'store/slices/authSlice';
import { formatDate, formatDateTime, formatInTz } from 'utils/dateFormat';

const LS_KEY = 'displayTimezone';
const FALLBACK_TZ = 'America/Chicago'; // Austin TX = America/Chicago

/**
 * Resolución de TZ activa, en orden de prioridad:
 *  1. user.displayTimezone (persistido en DB).
 *  2. localStorage.displayTimezone (fallback si el endpoint user falla
 *     o si se cambia antes de saber del user).
 *  3. defaultTimezone (env del backend via /config/public, default Austin).
 *  4. FALLBACK_TZ hardcoded.
 */
const TimezoneContext = createContext({
  timezone: FALLBACK_TZ,
  defaultTimezone: FALLBACK_TZ,
  setTimezone: () => {},
  resetTimezone: () => {},
  ready: false,
  fDate: () => '',
  fDateTime: () => '',
  fmt: () => '',
});

export const TimezoneProvider = ({ children }) => {
  const dispatch = useDispatch();
  const user = useSelector((s) => s.auth?.me);
  const [defaultTimezone, setDefaultTimezone] = useState(FALLBACK_TZ);
  const [localTimezone, setLocalTimezone] = useState(() => {
    try {
      return localStorage.getItem(LS_KEY) || null;
    } catch {
      return null;
    }
  });
  const [ready, setReady] = useState(false);

  // Bootstrap: traer la TZ default del backend
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await fetch({ url: '/config/public', method: 'get' });
        const tz = res?.defaultDisplayTimezone || FALLBACK_TZ;
        if (active) setDefaultTimezone(tz);
      } catch {
        /* fallback */
      } finally {
        if (active) setReady(true);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  // Resolución
  const timezone =
    user?.displayTimezone || localTimezone || defaultTimezone || FALLBACK_TZ;

  const setTimezone = useCallback(
    async (tz) => {
      // 1. Persistir localmente (respuesta inmediata, además ahorra al usuario
      //    pegar de nuevo a /auth/me cada vez).
      setLocalTimezone(tz);
      try {
        if (tz) localStorage.setItem(LS_KEY, tz);
        else localStorage.removeItem(LS_KEY);
      } catch {
        /* noop */
      }
      // 2. Persistir en DB si hay user logueado.
      if (user?.id) {
        try {
          await AuthService.updatePreferences({ displayTimezone: tz || null });
          // Refrescar el store para que el contexto lo vea desde el user
          dispatch(getMe());
        } catch {
          /* si falla, queda al menos en localStorage */
        }
      }
    },
    [user?.id, dispatch],
  );

  const resetTimezone = useCallback(async () => {
    setLocalTimezone(null);
    try {
      localStorage.removeItem(LS_KEY);
    } catch {
      /* noop */
    }
    if (user?.id) {
      try {
        await AuthService.updatePreferences({ displayTimezone: null });
        dispatch(getMe());
      } catch {
        /* noop */
      }
    }
  }, [user?.id, dispatch]);

  const value = useMemo(
    () => ({
      timezone,
      defaultTimezone,
      setTimezone,
      resetTimezone,
      ready,
      fDate: (d) => formatDate(d, timezone),
      fDateTime: (d) => formatDateTime(d, timezone),
      fmt: (d, format) => formatInTz(d, timezone, format),
    }),
    [timezone, defaultTimezone, setTimezone, resetTimezone, ready],
  );

  return (
    <TimezoneContext.Provider value={value}>
      {children}
    </TimezoneContext.Provider>
  );
};

export const useTimezone = () => useContext(TimezoneContext);

export default TimezoneContext;
